<script setup lang="ts">
import { ref, watch } from 'vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import TimestampWithIso from '@/components/ui/TimestampWithIso.vue';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import type { Card } from '@/integrations/Immersve/cards/types';
import {
  BLOCKED_TONE_CLASSES,
  STATUS_TONE_CLASSES,
  displayStatus,
  expiryLabel,
  maskedPan,
  seriesOf,
} from './cardDisplay';

// An account rarely has more, so one page is the usual case.
const PAGE_SIZE = 100;

// Given a series ID, the table shows only that series, oldest first. A
// reissued card keeps its predecessor's series ID and account, but amethyst
// can neither list a series nor name a card's predecessor, so the series is
// picked out of all the account's cards.
const props = defineProps<{ accountId: string; seriesId?: string }>();

const api = useImmersveApiClient();
const cards = ref<Card[]>([]);
const nextCursor = ref<string | undefined>();
const loading = ref(true);
const loadingMore = ref(false);
const error = ref<Error | null>(null);

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

async function load() {
  loading.value = true;
  error.value = null;
  cards.value = [];
  nextCursor.value = undefined;
  try {
    if (props.seriesId) {
      cards.value = await loadSeries(props.seriesId);
    } else {
      const page = await api.listCards(props.accountId, { limit: PAGE_SIZE });
      cards.value = page.items;
      nextCursor.value = page.pageInfo?.nextCursor;
    }
  } catch (err) {
    error.value = toError(err);
  } finally {
    loading.value = false;
  }
}

async function loadSeries(seriesId: string): Promise<Card[]> {
  const accountCards: Card[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.listCards(props.accountId, { limit: PAGE_SIZE, cursor });
    accountCards.push(...page.items);
    cursor = page.pageInfo?.nextCursor;
  } while (cursor);
  return accountCards
    .filter((card) => seriesOf(card) === seriesId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function loadMore() {
  loadingMore.value = true;
  try {
    const page = await api.listCards(props.accountId, {
      limit: PAGE_SIZE,
      cursor: nextCursor.value,
    });
    cards.value = [...cards.value, ...page.items];
    nextCursor.value = page.pageInfo?.nextCursor;
  } catch (err) {
    error.value = toError(err);
  } finally {
    loadingMore.value = false;
  }
}

watch(() => [props.accountId, props.seriesId], load, { immediate: true });
</script>

<template>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div
      class="hidden grid-cols-[1.6fr_1.4fr_1fr_0.6fr_2fr] bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase md:grid"
    >
      <div class="px-4 py-2">Card ID</div>
      <div class="px-4 py-2">Card</div>
      <div class="px-4 py-2">Status</div>
      <div class="px-4 py-2">Expiry</div>
      <div class="px-4 py-2">Created</div>
    </div>
    <div
      v-if="loading"
      data-test="cards-loading"
      class="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="cards-error" class="px-4 py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <p
      v-else-if="cards.length === 0"
      data-test="cards-empty"
      class="px-4 py-14 text-center text-sm text-gray-500"
    >
      {{ seriesId ? 'This account has no cards in this series.' : 'This account has no cards.' }}
    </p>
    <template v-else>
      <ul role="list">
        <li
          v-for="card in cards"
          :key="card.id"
          data-test="card-row"
          class="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-gray-100 px-4 py-3 last:border-b-0 md:grid-cols-[1.6fr_1.4fr_1fr_0.6fr_2fr] md:gap-0 md:px-0"
        >
          <div class="col-span-2 flex min-w-0 items-center gap-1.5 md:col-span-1 md:px-4">
            <span class="truncate font-mono text-xs text-gray-500">{{ card.id }}</span>
            <CopyButton :value="card.id" label="card ID" />
          </div>
          <div class="col-span-2 flex min-w-0 flex-col justify-center md:col-span-1 md:px-4">
            <RouterLink
              :to="`/cards/${encodeURIComponent(card.id)}`"
              class="font-mono text-sm font-medium text-indigo-600 hover:text-indigo-700"
              data-test="card-pan"
            >
              {{ maskedPan(card) }}
            </RouterLink>
            <span class="text-xs text-gray-500 capitalize" data-test="card-type">
              {{ card.type }}{{ card.currency ? ` · ${card.currency}` : '' }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-1.5 md:px-4">
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset"
              :class="STATUS_TONE_CLASSES[displayStatus(card)]"
              data-test="card-status"
            >
              {{ displayStatus(card) }}
            </span>
            <span
              v-if="card.isBlocked"
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset"
              :class="BLOCKED_TONE_CLASSES"
              data-test="card-blocked"
            >
              Frozen
            </span>
          </div>
          <div
            class="flex items-center font-mono text-sm text-gray-700 md:px-4"
            data-test="card-expiry"
          >
            {{ expiryLabel(card) }}
          </div>
          <div class="col-span-2 flex min-w-0 items-center md:col-span-1 md:px-4">
            <TimestampWithIso :value="card.createdAt" />
          </div>
        </li>
      </ul>
      <div v-if="nextCursor" class="border-t border-gray-100 px-4 py-3 text-center">
        <button
          type="button"
          data-test="cards-load-more"
          class="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </template>
  </div>
</template>
